import { isBrowsing } from './browse-mode.ts';
import type {
  AssignmentConfig,
  Message,
  SiteMetadata,
  TestCaseResult,
  TestResults,
} from './evaluator-types.ts';
import { postJsonWithRetry } from './fetch-helpers.ts';
import javascriptEvaluator from './javascript-evaluator.ts';
import { pageLocation } from './storage.ts';

/*
 * The evaluator for JavaScript unit-test assignments (a testcases.js next to
 * the page). Two runs happen concurrently:
 *
 * - The browser run (the wrapped javascript-evaluator): powers the REPL,
 *   error display, and the pills — execute() resolves with its results as
 *   soon as it finishes, so feedback is instant.
 *
 * - The witnessed run: the same code goes to /api/submissions/ with
 *   language: "javascript"; the server re-runs it against its own copy of
 *   the testcases on the runner's Node harness and records the result in
 *   test_runs (plans/done/server-side-js-grading.md). This is the run that
 *   counts — the browser one can be tampered with (override getResults,
 *   patch the reference impls) but only ever affects the local display.
 *
 * When the witnessed run lands it is reconciled against the browser's: if
 * any function's verdict differs (missing / all passing / some failing —
 * case-level comparison would be meaningless, since testcases are often
 * randomly generated and the two runs draw different cases), the student is
 * warned and the pills are updated to the server's results via
 * onServerResults, so what they see can't silently disagree with what the
 * gradebook grades from.
 *
 * If the witnessed run is unavailable (no runner configured, network
 * trouble, or a page outside a course) the browser results stand,
 * unrecorded — same feedback-only behavior as before.
 */

// The pill-level verdict for one function's results.
const verdict = (cases: TestCaseResult[] | null | undefined) =>
  cases == null ? 'missing' : cases.every((c) => c.passed) ? 'pass' : 'fail';

const sameVerdicts = (a: TestResults = {}, b: TestResults = {}) => {
  const names = new Set([...Object.keys(a), ...Object.keys(b)]);
  return [...names].every((n) => verdict(a[n]) === verdict(b[n]));
};

class WitnessedJsEvaluator {
  inner: ReturnType<typeof javascriptEvaluator>;
  message: Message;
  serverUrl: string | null;

  // Wired up by index.ts: how to re-render the test results display when
  // the witnessed run disagrees with the browser run.
  onServerResults: ((results: TestResults) => void) | null = null;

  constructor(
    config: AssignmentConfig,
    message: Message,
    username: string,
    metadata: SiteMetadata,
  ) {
    this.inner = javascriptEvaluator(config, message, username, metadata);
    this.message = message;
    // '' is a valid serverUrl meaning same-origin (the runner proxy); only
    // null/undefined mean there's no runner at all.
    this.serverUrl = metadata.serverUrl ?? null;
  }

  async execute(code: string, source: string, others: { file: string; content: string }[] = []) {
    // Kick off the witnessed run first so it overlaps the browser run
    // instead of queueing behind it; it never rejects.
    const witnessed = this.#serverResults(code, others);

    // The browser run's value: for a unit-test page, the results map the
    // harness's getResults produced (dynamic data from running student code).
    let local: TestResults | undefined;
    let localError: unknown;
    let localFailed = false;
    try {
      local = (await this.inner.execute(code, source, others)) as TestResults | undefined;
    } catch (e) {
      localFailed = true;
      localError = e;
    }

    witnessed.then((server) => {
      if (server === null) return;
      if (!sameVerdicts(localFailed ? {} : local, server)) {
        this.message(
          "The server's test results differ from this browser's run — showing the " +
            "server's results, which are what count.",
          undefined,
          'error',
        );
        this.onServerResults?.(server);
      }
    });

    if (localFailed) throw localError;
    return local;
  }

  // Test results from the witnessed server run, or null when there's nothing
  // to be had (not a course page, no runner, or the runner couldn't run the
  // code — in the 422 case the browser has already shown the same error).
  // Runs in the background, so 503 retries are silent — no minibuffer
  // chatter after the student already has their local results.
  async #serverResults(
    code: string,
    others: { file: string; content: string }[],
  ): Promise<TestResults | null> {
    const { courseId, dir } = pageLocation();
    if (this.serverUrl == null || !courseId || !dir) return null;

    const extraSources = others.map((o) => o.content);
    // A run while browsing an old revision is off the record — the proxy
    // still witnesses (for the display cross-check) but records nothing
    // (plans/delete-rename-files.md).
    const body = JSON.stringify({
      language: 'javascript',
      code,
      ...(extraSources.length ? { extraSources } : {}),
      courseId,
      assignment: dir,
      ...(isBrowsing() ? { browse: true } : {}),
    });

    try {
      const response = await postJsonWithRetry(
        `${this.serverUrl}/api/submissions/`,
        body,
        () => {},
      );
      if (response.status === 200 && response.results) return response.results;
      console.log(`Server test run not usable (status ${response.status})`, response);
      return null;
    } catch (e) {
      console.log('Server test run unavailable; browser results stand, unrecorded.', e);
      return null;
    }
  }
}

const evaluator = (
  config: AssignmentConfig,
  message: Message,
  username: string,
  metadata: SiteMetadata,
) => new WitnessedJsEvaluator(config, message, username, metadata);

export default evaluator;
